/**
 * breakpoint
 * 
 * @author jjarolim
 */

interface Breakpoint {
    name: string;
    min: number;
    max: number;
}

const breakpoints: Breakpoint[] = [
    { name: 'xs', min: 0, max: 575 },
    { name: 'sm', min: 576, max: 767 },
    { name: 'md', min: 768, max: 991 },
    { name: 'lg', min: 992, max: 1199 },
    { name: 'xl', min: 1200, max: 1613 },
    { name: '2xl', min: 1400, max: 1903 },
    { name: '3xl', min: 1904, max: 9999 },
]

interface BreakpointChange {
    from: Breakpoint | null;
    to: Breakpoint | null;
}

type BreakpointCallback = (change: BreakpointChange) => void;

const getBreakpoint = (): Breakpoint | null => {
    for (let i = 0; i < breakpoints.length; i++) {
        const breakpoint = breakpoints[i];
        const mediaQuery = `(min-width: ${breakpoint.min}px) and (max-width: ${breakpoint.max}px)`;
        if (window.matchMedia(mediaQuery).matches) {
            return breakpoint;
        }
    }
    return null;
}

const watchBreakpoint = (callback: BreakpointCallback): (() => void) => {
    let last_breakpoint = getBreakpoint();
    const handlers: Array<{ mql: MediaQueryList; handler: (event: MediaQueryListEvent) => void }> = [];

    // Create a MediaQueryList for each breakpoint
    for (let i = 0; i < breakpoints.length; i++) {
        const breakpoint = breakpoints[i];
        const mediaQuery = `(min-width: ${breakpoint.min}px) and (max-width: ${breakpoint.max}px)`;
        const mql = window.matchMedia(mediaQuery);

        // Create handler for this breakpoint
        const handler = (_event: MediaQueryListEvent) => {
            const current_breakpoint = getBreakpoint();
            if (current_breakpoint?.name !== last_breakpoint?.name) {
                callback({ from: last_breakpoint, to: current_breakpoint });
                last_breakpoint = current_breakpoint;
            }
        };

        mql.addEventListener('change', handler);
        handlers.push({ mql, handler });
    }

    // Return cleanup function
    return () => {
        handlers.forEach(({ mql, handler }) => {
            mql.removeEventListener('change', handler);
        });
    };
}

export {
    getBreakpoint,
    watchBreakpoint
}

export type {
    Breakpoint,
    BreakpointChange,
    BreakpointCallback
}
